import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import {
  getActiveSubscriptionPrice,
  hasAvailableSubscriptionsForSegment,
  MobileProduct,
  SimInfoResource,
} from '@dch/products-business-logic';
import { CustomerAccount } from '@proximus/common-business-logic';
import { getPiiProduct, savePii, getPiiServiceId } from '../../../../shared/pii/pii.utils';
import { TranslationService } from '@proximus/ng-common';
import { Store } from '@ngrx/store';
import { ApplicationState, getUserProduct } from '../../../../store/reducers';
import { OnDestroyHookDirective } from '../../../../shared/hooks';
import { EwebTokenService } from '../../../../services/eweb-token.service';
import * as ProductsActions from '../../../../store/actions/products.actions';
import { filter, take } from 'rxjs/operators';

@Component({
  selector: 'mprd-pricing-plan',
  templateUrl: './pricing-plan.component.html',
  styleUrls: ['./pricing-plan.component.css'],
})
export class PricingPlanComponent extends OnDestroyHookDirective implements OnChanges {
  @Input() simInfo: SimInfoResource;
  @Input() pending: boolean;
  @Input() simInfoError: boolean;
  @Input() customerAccount: CustomerAccount;
  @Input() mobileProductDetails: MobileProduct;
  @Output()
  getSimInfoForAccessNumber: EventEmitter<string> = new EventEmitter();
  simCardNumberVisible = false;
  pukVisible = false;
  price: number;
  hasAvailableSubscriptions: boolean;

  savePii = savePii;

  constructor(
    private readonly translationService: TranslationService,
    private readonly store: Store<ApplicationState>,
    private readonly ewebTokenService: EwebTokenService
  ) {
    super();
  }

  public getPIIObject() {
    return getPiiProduct();
  }

  public navigateToSwapSubscription(accessNumber: string): void {
    savePii(accessNumber);
    const serviceId = getPiiServiceId();

    if (!serviceId) {
      console.error('No serviceId found in session storage');
      return;
    }

    this.addSubscription(
      this.store.select(getUserProduct).pipe(
        take(1)
      ).subscribe((userProduct) => {
        // Check if userProduct is loaded, if not, dispatch GetProducts action
        if (!userProduct?.loaded) {
          const appBrand = this.translationService.getTranslatedValueFor('app.brand');
          this.store.dispatch(
            new ProductsActions.GetProducts({
              appBrand: appBrand || 'proximus',
            })
          );

          // Wait for products to load and then proceed
          this.addSubscription(
            this.store.select(getUserProduct).pipe(
              filter((up) => up?.loaded),
              take(1)
            ).subscribe((loadedUserProduct) => {
              this.proceedWithSwapSubscription(loadedUserProduct, serviceId);
            })
          );
        } else {
          // Products already loaded, proceed directly
          this.proceedWithSwapSubscription(userProduct, serviceId);
        }
      })
    );
  }

  private proceedWithSwapSubscription(userProduct: any, serviceId: string): void {
    if (userProduct?.loaded) {
      // Get the ewebToken for the serviceId using the service
      const ewebToken = this.ewebTokenService.getEwebTokenByServiceId(
        userProduct.packs,
        userProduct.outOfPacks,
        serviceId
      );

      if (!ewebToken) {
        console.error('No ewebToken found for serviceId:', serviceId);
        return;
      }

      const baseUrl = this.translationService.getTranslatedValueFor('products.addProduct.eshare.url');
      const url = `${baseUrl}?token=${ewebToken}&type=jwt&intent=mobile&action=SWAP_SUBSCRIPTION`;
      window.open(url, '_self');
    }
  }


  ngOnChanges(): void {
    if (this.mobileProductDetails) {
      this.price = getActiveSubscriptionPrice(
        this.mobileProductDetails,
        this.customerAccount,
      );
      if (this.customerAccount && this.customerAccount.getSelectedCustomer()) {
        this.hasAvailableSubscriptions = hasAvailableSubscriptionsForSegment(
          this.mobileProductDetails,
          this.customerAccount.getSelectedCustomer().subsegment,
        );
      }
    }
  }

  public getSimInfo() {
    this.getSimInfoForAccessNumber.emit(this.mobileProductDetails.accessNumber);
  }

  public toggleSimCardNumberVisibility() {
    this.simCardNumberVisible = !this.simCardNumberVisible;
    if (this.simCardNumberVisible) {
      this.getSimInfo();
    }
  }

  public togglePukVisibility() {
    this.pukVisible = !this.pukVisible;
    if (this.pukVisible) {
      this.getSimInfo();
    }
  }

  public getPrice() {
    return this.price ? this.price.toString() : '0';
  }
}
